/**
 * NIP-57 Lightning Zaps Service
 * 
 * Handles Lightning Network payments through LNURL and WebLN
 * 
 * Flow:
 * 1. Decode lnurl/lud16 to get callback URL
 * 2. Create Zap Request (Kind 9734)
 * 3. Send to LNURL callback to get invoice
 * 4. Pay invoice via WebLN or display QR
 * 5. LNURL server publishes Zap Receipt (Kind 9735)
 */

import { Event, finalizeEvent } from 'nostr-tools'
import { bech32 } from '@scure/base'
import { getNostrPool } from '../lib/nostrPool'
import { DEFAULT_RELAYS } from '../config/constants'

interface ZapParams {
  recipientPubkey: string
  lnAddress: string // lud16 format: user@domain.com
  amount: number // in sats
  comment?: string
  eventId?: string // optional event being zapped
  sk?: Uint8Array | null
}

interface LnurlPayResponse {
  callback: string
  maxSendable: number
  minSendable: number
  metadata: string
  allowsNostr?: boolean
  nostrPubkey?: string
}

interface InvoiceResponse {
  pr: string // payment request (invoice)
  routes?: any[]
}

/**
 * Parse lud16 (Lightning Address) to LNURL endpoint
 */
export function lud16ToLnurlEndpoint(lud16: string): string {
  const [name, domain] = lud16.split('@')
  if (!name || !domain) {
    throw new Error('Geçersiz Lightning adresi formatı')
  }
  return `https://${domain}/.well-known/lnurlp/${name}`
}

/**
 * Decode bech32 LNURL to URL
 */
export function decodeLnurl(lnurl: string): string {
  try {
    const { words } = bech32.decode(lnurl.toLowerCase(), 2000)
    const data = bech32.fromWords(words)
    return new TextDecoder().decode(new Uint8Array(data))
  } catch {
    throw new Error('Geçersiz LNURL')
  }
}

/**
 * Fetch LNURL pay endpoint metadata
 */
export async function fetchLnurlPay(lnAddress: string): Promise<LnurlPayResponse> {
  let endpoint: string

  // Check if it's lud16 (email-like) or lnurl (bech32)
  if (lnAddress.includes('@')) {
    endpoint = lud16ToLnurlEndpoint(lnAddress)
  } else if (lnAddress.toLowerCase().startsWith('lnurl')) {
    endpoint = decodeLnurl(lnAddress)
  } else {
    throw new Error('Geçersiz Lightning adresi')
  }

  const response = await fetch(endpoint)
  if (!response.ok) {
    throw new Error('Lightning servisine ulaşılamadı')
  }

  const data = await response.json()
  
  if (data.status === 'ERROR') {
    throw new Error(data.reason || 'LNURL hatası')
  }

  return data as LnurlPayResponse
}

/**
 * Create a NIP-57 Zap Request event (Kind 9734)
 */
export async function createZapRequest({
  recipientPubkey,
  lnAddress,
  amount,
  comment,
  eventId,
  sk
}: ZapParams): Promise<{ event: Event; encoded: string }> {
  const amountMsats = amount * 1000 // Convert to millisats

  // Build tags
  const tags: string[][] = [
    ['relays', ...DEFAULT_RELAYS],
    ['amount', amountMsats.toString()],
    ['p', recipientPubkey],
  ]

  // Add event tag if zapping a specific event
  if (eventId) {
    tags.push(['e', eventId])
  }

  // Add lnurl tag
  const lnurlEndpoint = lnAddress.includes('@') 
    ? lud16ToLnurlEndpoint(lnAddress)
    : decodeLnurl(lnAddress)
  tags.push(['lnurl', lnurlEndpoint])

  const eventTemplate = {
    kind: 9734,
    created_at: Math.floor(Date.now() / 1000),
    tags,
    content: comment || ''
  }

  let signedEvent: Event

  if (sk) {
    signedEvent = finalizeEvent(eventTemplate, sk) as Event
  } else if (window.nostr) {
    signedEvent = await window.nostr.signEvent(eventTemplate) as Event
  } else {
    throw new Error('Zap göndermek için giriş yapmalısınız')
  }

  // Encode for URL
  const encoded = encodeURIComponent(JSON.stringify(signedEvent))

  return { event: signedEvent, encoded }
}

/**
 * Get invoice from LNURL callback with Zap Request
 */
export async function getZapInvoice(
  callback: string,
  amount: number,
  zapRequest: string,
  comment?: string
): Promise<string> {
  const amountMsats = amount * 1000
  
  const url = new URL(callback)
  url.searchParams.set('amount', amountMsats.toString())
  url.searchParams.set('nostr', zapRequest)
  
  if (comment) {
    url.searchParams.set('comment', comment)
  }

  const response = await fetch(url.toString())
  if (!response.ok) {
    throw new Error('Fatura alınamadı')
  }

  const data: InvoiceResponse = await response.json()
  
  if (!data.pr) {
    throw new Error('Geçersiz fatura yanıtı')
  }

  return data.pr
}

/**
 * Pay invoice using WebLN if available
 */
export async function payWithWebLN(invoice: string): Promise<{ preimage: string }> {
  if (!window.webln) {
    throw new Error('WebLN mevcut değil')
  }

  try {
    await window.webln.enable()
    const result = await window.webln.sendPayment(invoice)
    return { preimage: result.preimage }
  } catch (error) {
    throw new Error('Ödeme yapılamadı')
  }
}

/**
 * Main zap function - orchestrates the entire flow
 */
export async function sendZap(params: ZapParams): Promise<{
  success: boolean
  invoice?: string
  paid?: boolean
  error?: string
}> {
  try {
    // 1. Fetch LNURL metadata
    const lnurlData = await fetchLnurlPay(params.lnAddress)
    
    const amountMsats = params.amount * 1000
    if (amountMsats < lnurlData.minSendable || amountMsats > lnurlData.maxSendable) {
      return {
        success: false,
        error: `Miktar ${lnurlData.minSendable / 1000} - ${lnurlData.maxSendable / 1000} sats aralığında olmalı`
      }
    }

    // 2. Create Zap Request
    const { encoded } = await createZapRequest(params)

    // 3. Get invoice
    const invoice = await getZapInvoice(
      lnurlData.callback,
      params.amount,
      encoded,
      params.comment
    )

    // 4. Try to pay with WebLN
    if (window.webln) {
      try {
        await payWithWebLN(invoice)
        return { success: true, invoice, paid: true }
      } catch {
        // WebLN failed, return invoice for manual payment
        return { success: true, invoice, paid: false }
      }
    }

    // No WebLN, return invoice for QR display
    return { success: true, invoice, paid: false }

  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Zap gönderilemedi'
    }
  }
}

/**
 * Check if WebLN is available
 */
export function hasWebLN(): boolean {
  return typeof window !== 'undefined' && !!window.webln
}

/**
 * Fetch received zaps for a pubkey (Kind 9735)
 */
export async function fetchReceivedZaps(pubkey: string): Promise<Event[]> {
  const pool = getNostrPool()
  
  return new Promise((resolve) => {
    const zaps: Event[] = []
    
    const filter = {
      kinds: [9735],
      '#p': [pubkey],
      limit: 50
    }
    
    const sub = pool.subscribeMany(DEFAULT_RELAYS, filter, {
      onevent(event: Event) {
        zaps.push(event)
      },
      oneose() {
        zaps.sort((a, b) => b.created_at - a.created_at)
        resolve(zaps)
        sub.close()
      }
    })

    setTimeout(() => {
      sub.close()
      resolve(zaps)
    }, 5000)
  })
}

// Extend Window for WebLN
declare global {
  interface Window {
    webln?: {
      enable(): Promise<void>
      sendPayment(invoice: string): Promise<{ preimage: string }>
    }
  }
}
